import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  FormHelperText,
  Grid,
  InputLabel,
  OutlinedInput,
  Paper,
  Stack,
  Typography
} from "@mui/material";
import * as Yup from "yup";
import { Formik } from "formik";
import axios from "axios";
import { baseURL } from "../../api";
import { Global_Data } from "../../globalData/GlobalData";
import { toast } from "react-toastify";
import CustomAlert from "../../component/AlertComponent/Alert";
import { setAccessToken } from "../../auth/authClient";

const TwoFaRequired = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const loginResponseData = location.state?.loginResponse?.data || null;

  const { setToken, setUserData } = Global_Data?.() || {};

  const [isResendEnabled, setIsResendEnabled] = React.useState(false);
  const [isResending, setIsResending] = React.useState(false);
  const [timeRemaining, setTimeRemaining] = React.useState(300); // 5 minutes in seconds

  // Extract 2FA data from login response
  const twoFaId = loginResponseData?.["2FA-ID"];
  const twoFaInputMessage = loginResponseData?.["2FA-InputMessage"]
  const twoFaExpiryMessage = loginResponseData?.["2FA-ExpiryMessage"] 
  const twoFaType = loginResponseData?.["2FA-Type"]

  // Redirect to login if no data
  useEffect(() => {
    if (!loginResponseData || !twoFaId) {
      CustomAlert("error", "Invalid 2FA session. Please login again.");
      navigate("/login");
    }
  }, [loginResponseData, twoFaId, navigate]);

  // Timer to enable resend button after 5 minutes
  useEffect(() => {
    if (timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            setIsResendEnabled(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    } else {
      setIsResendEnabled(true);
    }
  }, [timeRemaining]);

  const findFirstMenuRoute = (dataset) => {
    for (const menu of dataset) {
      if (menu.hasOwnProperty("sMenuRoute") && menu.sMenuRoute && menu.sMenuRoute !== "") {
        return menu.sMenuRoute;
      }
      if (menu.hasOwnProperty("submenus") && Array.isArray(menu.submenus) && menu.submenus.length > 0) {
        const route = findFirstMenuRoute(menu.submenus);
        if (route) return route;
      }
    }
    return "";
  };

  function fetchDrawerRoute(token) {
    return axios
      .get(`${baseURL}/menu/get/DRAWER`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      .then((result) => {
        return findFirstMenuRoute(result?.data?.data?.menu) || "";
      })
      .catch((error) => {
        console.error("Error fetching menu:", error);
        return "";
      });
  }

  const handleResendOTP = async () => {
    if (!twoFaId || !twoFaType) {
      CustomAlert("error", "Unable to resend OTP. Please try logging in again.");
      return;
    }

    setIsResending(true);
    try {
      const payload = {
        "2FA-ID": String(twoFaId)
      };

      axios
        .post(`${baseURL}/user/2FA/request/${twoFaType}`, payload, {
          headers: {
            mode: "cors",
            "Access-Control-Allow-Origin": "*",
            "Access-Control-Allow-Methods": "GET,PUT,POST,DELETE,PATCH,OPTIONS",
            "Content-Type": "application/json"
          }
        })
        .then((response) => {
          if (response.status === 200) {
            toast.success(
              response?.data?.metadata?.msg || "OTP has been resent successfully",
              {
                position: "top-center"
              }
            );
            // Reset timer after successful resend
            setIsResendEnabled(false);
            setTimeRemaining(300);
          }
          setIsResending(false);
        })
        .catch((error) => {
          let errorMessage = "Failed to resend OTP. Please try again.";

          if (error?.response) {
            const serverMsg = error?.response?.data?.metadata?.msg;
            if (serverMsg) {
              errorMessage = serverMsg;
            } else if (error.response.status === 400) {
              errorMessage = "Invalid request. Please try again.";
            } else if (error.response.status === 404) {
              errorMessage = "2FA session not found. Please login again.";
            }
          } else if (error?.request) {
            if (!navigator.onLine) {
              errorMessage = "No internet connection. Please check your network.";
            } else {
              errorMessage = "Unable to connect to server. Please try again later.";
            }
          }

          CustomAlert("error", errorMessage, {
            position: "top-center"
          });
          setIsResending(false);
        });
    } catch (err) {
      const errorMessage =
        err?.response?.data?.metadata?.msg ||
        "An unexpected error occurred. Please try again.";
      CustomAlert("error", errorMessage, {
        position: "top-center"
      });
      setIsResending(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Grid
        container
        direction="column"
        justifyContent="flex-end"
        sx={{
          minHeight: "100vh"
        }}
      >
        <Grid item xs={12} sx={{ ml: 3, mt: 1 }}>
          <img
            width={"200px"}
            src={"http://13.231.17.170:8080/images/enterprise/get?filename=wilstark_logo.png"}
          />
          <Box sx={{ width: "500px" }}></Box>
        </Grid>

        <Grid item xs={12}>
          <Grid
            item
            xs={12}
            container
            justifyContent="center"
            alignItems="center"
            sx={{ minHeight: { xs: "calc(100vh - 134px)", md: "calc(100vh - 112px)" } }}
          >
            <Grid item sx={{ maxWidth: "500px" }}>
              <Paper className="p-5">
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="h5" sx={{ mb: { xs: -0.5, sm: 0.5 } }}>
                      OTP
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Formik
                      initialValues={{
                        otp: ""
                      }}
                      validationSchema={Yup.object().shape({
                        otp: Yup.string()
                          .required("OTP is required")
                          .matches(/^\d+$/, "OTP must contain only numbers")
                          .min(4, "OTP must be at least 4 digits")
                          .max(10, "OTP must not exceed 10 digits")
                      })}
                      onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
                        try {
                          const payload = {
                            "2FA-ID": String(twoFaId),
                            "2FA-Value": values.otp
                          };

                          axios
                            .post(`${baseURL}/user/2FA/validate/${twoFaType}`, payload, {
                              headers: {
                                mode: "cors",
                                "Access-Control-Allow-Origin": "*",
                                "Access-Control-Allow-Methods": "GET,PUT,POST,DELETE,PATCH,OPTIONS",
                                "Content-Type": "application/json"
                              }
                            })
                            .then((response) => {
                              // If response status is 200, proceed with normal login
                              if (response.status === 200) {
                                setToken(response?.data?.data?.access_token);
                                setUserData(response?.data?.data);
                                setAccessToken(response?.data?.data?.access_token);
                                localStorage.setItem("userData", JSON.stringify(response?.data?.data));
                                
                                toast.success(
                                  response?.data?.metadata?.msg || "OTP verified successfully",
                                  {
                                    position: "top-center"
                                  }
                                );

                                fetchDrawerRoute(response?.data?.data?.access_token).then((route) => {
                                  navigate(route);
                                });
                              }
                            })
                            .catch((error) => {
                              let errorMessage = "OTP validation failed. Please try again.";

                              if (error?.response) {
                                const serverMsg = error?.response?.data?.metadata?.msg;
                                if (serverMsg) {
                                  errorMessage = serverMsg;
                                } else if (error.response.status === 401) {
                                  errorMessage = "Invalid OTP. Please try again.";
                                } else if (error.response.status === 400) {
                                  errorMessage = "Invalid OTP format.";
                                }
                              } else if (error?.request) {
                                if (!navigator.onLine) {
                                  errorMessage = "No internet connection. Please check your network.";
                                } else {
                                  errorMessage = "Unable to connect to server. Please try again later.";
                                }
                              }

                              CustomAlert("error", errorMessage, {
                                position: "top-center"
                              });
                              setStatus({ success: false });
                              setSubmitting(false);
                            });
                        } catch (err) {
                          setStatus({ success: false });
                          const errorMessage =
                            err?.response?.data?.metadata?.msg ||
                            "An unexpected error occurred. Please try again.";
                          CustomAlert("error", errorMessage, {
                            position: "top-center"
                          });
                          setSubmitting(false);
                        }
                      }}
                    >
                      {({
                        errors,
                        handleBlur,
                        handleChange,
                        handleSubmit,
                        isSubmitting,
                        touched,
                        values
                      }) => (
                        <form noValidate onSubmit={handleSubmit}>
                          <Grid container spacing={3}>
                            <Grid item xs={12}>
                              <Stack spacing={1}>
                                <InputLabel htmlFor="otp-input">{twoFaInputMessage}</InputLabel>
                                <OutlinedInput
                                  id="otp-input"
                                  type="text"
                                  name="otp"
                                  value={values.otp}
                                  onBlur={handleBlur}
                                  onChange={handleChange}
                                  placeholder="Enter OTP"
                                  fullWidth
                                  error={Boolean(touched.otp && errors.otp)}
                                  inputProps={{
                                    maxLength: 10,
                                    pattern: "[0-9]*",
                                    inputMode: "numeric"
                                  }}
                                />
                                {touched.otp && errors.otp && (
                                  <FormHelperText error id="standard-weight-helper-text-otp">
                                    {errors.otp}
                                  </FormHelperText>
                                )}
                              </Stack>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary">
                                {twoFaExpiryMessage}
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Stack spacing={2}>
                                <Button
                                  disableElevation
                                  disabled={isSubmitting}
                                  fullWidth
                                  size="large"
                                  type="submit"
                                  variant="contained"
                                  color="primary"
                                >
                                  Submit
                                </Button>
                                <Button
                                  disableElevation
                                  disabled={!isResendEnabled || isResending || isSubmitting}
                                  fullWidth
                                  size="medium"
                                  variant="outlined"
                                  color="primary"
                                  onClick={handleResendOTP}
                                >
                                  {isResending
                                    ? "Resending..."
                                    : isResendEnabled
                                    ? `Resend ${twoFaType || "OTP"}`
                                    : `Resend ${twoFaType || "OTP"} (${Math.floor(timeRemaining / 60)}:${String(timeRemaining % 60).padStart(2, "0")})`}
                                </Button>
                              </Stack>
                            </Grid>
                          </Grid>
                        </form>
                      )}
                    </Formik>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TwoFaRequired;
